import React, { Fragment, ReactNode } from "react";
import { DropdownToggle, DropdownMenu, Nav, Button, NavItem, NavLink, UncontrolledButtonDropdown } from "reactstrap";
import { User, ChevronDown, LogOut } from "lucide-react";
import { utils } from "../../../utils/utils";
import history from "../../../history";
import EventEmitter, { Events } from "../../../services/eventemitter";
import { operatorsApiService } from "../../../services/api/users/OperatorApiService";
import { fileService } from "../../../services/FileService";
import { authService } from "../../../services/authService";

interface UserBoxState {
    email: string;
    username: string;
    photo: string | null;
}

class UserBox extends React.Component<Record<string, never>, UserBoxState> {
    constructor(props: Record<string, never>) {
        super(props);

        this.state = {
            username: "",
            email: "",
            photo: "",
        };

        EventEmitter.on(Events.OnUserDataUpdate, this.updateUserData);
    }

    async componentDidMount() {
        this.updateUserData();
    }

    updateUserData = async () => {
        const userData = await operatorsApiService.get();

        if (!userData.success) {
            return;
        }

        let photo = null;
        if (userData.data.photoStorageId != null) {
            const photoResult = await fileService.GetFile(userData.data.photoStorageId);

            if (photoResult.success) {
                photo = photoResult.data;
            }
        }

        this.setState({
            username: userData.data.firstName + " " + userData.data.lastName,
            email: userData.data.email,
            photo: photo,
        });
    };

    logOut = async () => {
        try {
            await authService.signoutRedirect();
        } catch (error) {
            // signoutRedirect has already cleared the local session, so finish the logout in
            // the SPA rather than leaving the user on a page they are no longer signed in for.
            EventEmitter.emit(Events.onLogout);
        }
    };

    getImageSourceHeader = (): any => {
        if (this.state.photo) {
            return (
                <img
                    src={utils.appendBase64Data(this.state.photo)}
                    className="w-9 h-9 rounded-xl object-cover shadow-md ring-2 ring-white dark:ring-neutral-900"
                    alt=""
                />
            );
        }
        return (
            <div className="h-9 w-9 bg-gradient-to-br from-primary-600 to-primary-700 dark:from-primary-500 dark:to-primary-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow ring-2 ring-white dark:ring-neutral-900">
                <span className="text-sm font-semibold text-white">
                    {this.state.username ? this.state.username.charAt(0).toUpperCase() : <User size={14} strokeWidth={2} className="text-white" />}
                </span>
            </div>
        );
    };

    getImageSource = (): any => {
        if (!this.state?.photo) {
            return (
                <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-700 dark:from-primary-500 dark:to-primary-600 rounded-xl flex items-center justify-center shadow-md">
                    <User size={18} strokeWidth={2} className="text-white" />
                </div>
            );
        }

        return (
            <img
                className="w-10 h-10 rounded-full object-cover ring-2 ring-neutral-200 dark:ring-neutral-700"
                src={utils.appendBase64Data(this.state.photo!)}
                alt=""
            />
        );
    };

    render() {
        return (
            <Fragment>
                <div className="header-btn-lg pr-0">
                    <div className="widget-content p-0">
                        <div className="widget-content-wrapper">
                            <div className="widget-content-left">
                                <UncontrolledButtonDropdown>
                                    <DropdownToggle color="link" className="!inline-flex items-center !space-x-3 !px-3 !py-2 !rounded-xl hover:!bg-primary-50 dark:hover:!bg-primary-900/20 transition-all hover:!shadow-sm group">
                                        {this.getImageSourceHeader()}
                                        <div className="text-left hidden sm:block">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-neutral-100">{this.state.username}</div>
                                            <div className="text-xs text-gray-500 dark:text-neutral-400">{this.state.email}</div>
                                        </div>
                                        <ChevronDown size={16} strokeWidth={2} className="text-gray-400 dark:text-neutral-500 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
                                    </DropdownToggle>
                                    <DropdownMenu end className="rm-pointers dropdown-menu-lg !rounded-2xl !shadow-xl !border !border-neutral-200/50 dark:!border-neutral-800/50 !bg-white/95 dark:!bg-neutral-900/95 overflow-hidden backdrop-blur-xl">
                                        <div className="border-b border-primary-200/50 dark:border-primary-800/30">
                                            <div className="px-4 py-3 bg-gradient-to-r from-primary-50 to-primary-100/50 dark:from-primary-900/30 dark:to-primary-800/20">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                        {this.getImageSource()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-semibold text-neutral-900 dark:text-neutral-100 truncate">{this.state.username}</div>
                                                        <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{this.state.email}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="py-1">
                                            <Nav vertical>
                                                <NavItem>
                                                    <NavLink
                                                        href="#"
                                                        className="flex items-center gap-3 px-4 py-2.5 transition-all hover:!bg-primary-50 dark:hover:!bg-primary-900/20 !text-neutral-700 dark:!text-neutral-300 group"
                                                        onClick={() => history.push("/profile")}
                                                    >
                                                        <div className="p-1.5 rounded-lg bg-primary-100 dark:bg-primary-900/30 group-hover:bg-primary-200 dark:group-hover:bg-primary-900/50 transition-colors">
                                                            <User size={14} strokeWidth={1.75} className="text-primary-600 dark:text-primary-400" />
                                                        </div>
                                                        <span className="text-sm font-medium">Profile</span>
                                                    </NavLink>
                                                    <NavLink
                                                        href="#"
                                                        className="flex items-center gap-3 px-4 py-2.5 transition-all hover:!bg-danger-50 dark:hover:!bg-danger-900/20 !text-neutral-700 dark:!text-neutral-300 group"
                                                        onClick={this.logOut}
                                                    >
                                                        <div className="p-1.5 rounded-lg bg-danger-100 dark:bg-danger-900/30 group-hover:bg-danger-200 dark:group-hover:bg-danger-900/50 transition-colors">
                                                            <LogOut size={14} strokeWidth={1.75} className="text-danger-600 dark:text-danger-400" />
                                                        </div>
                                                        <span className="text-sm font-medium">Logout</span>
                                                    </NavLink>
                                                </NavItem>
                                            </Nav>
                                        </div>
                                    </DropdownMenu>
                                </UncontrolledButtonDropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default UserBox;
